import { createFileRoute, Link } from "@tanstack/react-router";
import {
  ArrowRight,
  CheckCircle2,
  Phone,
  Globe,
  PenTool,
  AtSign,
  MapPin,
  Users,
  PhoneCall,
  Bot,
  Sparkles,
  ClipboardList,
  Compass,
} from "lucide-react";
import { SiteLayout } from "@/components/SiteLayout";
import { PageHero } from "@/components/PageHero";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { buildFaqPageJsonLd } from "@/lib/serviceSchema";

const SITE_URL = "https://coloradowebimpressions.com";
const PATH = "/startup-bundle";
const URL = `${SITE_URL}${PATH}`;

const META_TITLE =
  "Colorado Startup Bundle | Website + CRM + Phone + AI in 30 Days | Colorado Web Impressions";
const META_DESCRIPTION =
  "Launch-in-a-Box for new Colorado businesses: website, branding, Google Business Profile, CRM, business phone, AI chatbot, and 90 days of content — one vendor, one bill, done in 30 days.";

const INCLUSIONS = [
  { icon: Globe, title: "Colorado Startup Website Bundle | Custom Site + SEO", body: "Mobile-first, fast, conversion-focused — built on the same stack we use for established Colorado businesses." },
  { icon: PenTool, title: "Branding & logo refresh", body: "A clean wordmark and color system you can actually use across web, print, vehicle wraps, and social." },
  { icon: AtSign, title: "Domain + business email", body: "We register your domain (or migrate it) and stand up professional Google Workspace email." },
  { icon: MapPin, title: "Google Business Profile setup", body: "Verified, fully optimized, with categories, services, photos, and the schema linkage to your site that AI engines need." },
  { icon: Users, title: "CRM + sales pipelines", body: "Lead capture, pipeline stages, automated follow-up sequences, and a single dashboard for every conversation." },
  { icon: PhoneCall, title: "Business phone + SMS", body: "A real business number with call routing, voicemail-to-text, and two-way SMS — separate from your personal cell." },
  { icon: Bot, title: "AI chatbot for your site", body: "A trained chatbot that answers customer questions, books appointments, and routes hot leads straight to your phone." },
  { icon: Sparkles, title: "GEO/AI optimization baseline", body: "Schema, FAQ structure, and entity work so ChatGPT, Gemini, and Perplexity can cite you from day one." },
  { icon: ClipboardList, title: "First 90 days of content", body: "Launch blog posts, service pages, and social copy — enough fuel to look established the moment you go live." },
];

const WHO_FOR = [
  { title: "New Colorado businesses launching from scratch", body: "You have an LLC (or are about to) and need every digital piece in place to look credible from day one." },
  { title: "Owners rebooting an outdated presence", body: "Your old site, email, and social are scattered across five vendors. We consolidate it into one stack you actually own." },
  { title: "Side hustles going full-time", body: "You're going from \"DM me\" to a real business. We give you the infrastructure to look like you've been doing this for a decade." },
];

const TIMELINE = [
  { step: "Week 1", title: "Discovery & strategy", body: "60-minute kickoff with Chris. We lock in branding direction, site structure, GBP categories, CRM pipeline stages, and the 90-day content angle." },
  { step: "Weeks 2–3", title: "Build sprint", body: "Site, branding, domain, email, GBP, CRM, phone, and chatbot all built and configured in parallel. Daily progress in your shared workspace." },
  { step: "Week 4", title: "Launch", body: "Site goes live, GBP goes verified, CRM goes hot, phone goes active. We walk you through every system on a 90-minute handover call." },
  { step: "Days 30–90", title: "Support runway", body: "Three follow-up calls, Slack/email access, content drops, and adjustments as your first leads start coming through." },
];

const VERSUS = [
  { label: "Five separate vendors", bad: "Web designer, brand person, GBP \"expert,\" CRM consultant, VoIP rep — each with their own contract, login, invoice, and finger to point when something breaks." },
  { label: "One Colorado vendor (us)", good: "One contract, one bill, one person (Chris) who owns the outcome. When anything breaks, you call one number — and it's a Colorado number." },
];

const FAQS = [
  { q: "How much does the Startup Bundle cost?", a: "Pricing starts at a flat fee that covers the full 30-day build plus the first 90 days of support. Exact pricing depends on scope (number of services, content volume, and whether you need branding from scratch or just a refresh). Book a discovery call and we'll quote you in writing within 48 hours." },
  { q: "How fast can I actually launch?", a: "Most Startup Bundles launch in 30 days from kickoff. Faster is possible if you have branding done; slower if you want a deeper content runway up front. We don't drag projects out — fast launches are a feature, not a compromise." },
  { q: "What if I already have a domain or website?", a: "Even better. We migrate what's worth keeping (domain, content, email) and rebuild what isn't. You won't pay for things you already have." },
  { q: "Do I own everything when we're done?", a: "Yes. Domain, website code, content, CRM data, phone number, GBP — all in your name, on your accounts. We don't hold anything hostage. If you ever leave, you walk away with everything." },
  { q: "Can I add the CRM and business phone later?", a: "Yes — but it's cheaper and faster to do it as part of the bundle. Bolting these on later means duplicate setup work and migrating leads you've already collected." },
  { q: "Is this only for Colorado Springs businesses?", a: "No. We build Startup Bundles for businesses anywhere in Colorado — Denver, Boulder, Fort Collins, Pueblo, Castle Rock, Parker, Loveland, and beyond. The whole thing is built remotely with on-site visits available in the El Paso County area." },
  { q: "What about ongoing SEO and marketing after launch?", a: "We can roll you onto a monthly SEO, GEO/AI, or full digital marketing retainer when you're ready — but we don't force it. Plenty of Startup Bundle clients run their own marketing for a year before adding services. The bundle is built to stand on its own." },
];

const SERVICE_JSON_LD = {
  "@context": "https://schema.org",
  "@type": "Service",
  name: "Colorado Startup Bundle (Launch-in-a-Box)",
  serviceType: "Business Launch Package",
  provider: {
    "@type": "LocalBusiness",
    name: "Colorado Web Impressions",
    url: SITE_URL,
    areaServed: { "@type": "State", name: "Colorado" },
  },
  areaServed: "Colorado",
  description:
    "All-in-one business launch package for new Colorado companies: website, branding, Google Business Profile, CRM, business phone, AI chatbot, GEO/AI optimization, and 90 days of content. One vendor, one bill, done in 30 days.",
  url: URL,
  offers: {
    "@type": "Offer",
    priceCurrency: "USD",
    availability: "https://schema.org/InStock",
    description: "Starting at a flat-fee 30-day build with 90 days of included support.",
  },
};

const BREADCRUMB_JSON_LD = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: SITE_URL },
    { "@type": "ListItem", position: 2, name: "Startup Bundle", item: URL },
  ],
};

export const Route = createFileRoute("/startup-bundle")({
  head: () => ({
    meta: [
        { name: "description", content: "All-in-one Colorado startup bundle: custom website, local SEO foundation, and Google Business Profile setup. Built for Colorado founders launching in 2026." },
      { title: META_TITLE },
      { name: "description", content: META_DESCRIPTION },
      { property: "og:title", content: META_TITLE },
      { property: "og:description", content: META_DESCRIPTION },
      { property: "og:url", content: URL },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(SERVICE_JSON_LD) },
      { type: "application/ld+json", children: JSON.stringify(BREADCRUMB_JSON_LD) },
      { type: "application/ld+json", children: JSON.stringify(buildFaqPageJsonLd(FAQS)) },
    ],
  }),
  component: StartupBundlePage,
});

function StartupBundlePage() {
  return (
    <SiteLayout>
      <PageHero
        eyebrow="Launch-in-a-Box"
        title={
          <>
            The Colorado <span className="text-brand-gold">Startup Bundle</span>
          </>
        }
        description="Everything a new Colorado business needs to launch online — website, branding, Google Business Profile, CRM, business phone, AI chatbot, and 90 days of content. One vendor, one bill, live in 30 days."
      >
        <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
          <Link
            to="/contact"
            search={{ topic: "startup-bundle" } as never}
            className="inline-flex items-center gap-2 rounded-md bg-gradient-gold px-6 py-3 text-base font-bold text-brand-black shadow-gold transition-spring hover:-translate-y-0.5"
          >
            Book a Launch Call
            <ArrowRight className="h-4 w-4" />
          </Link>
          <a
            href="tel:+17194193935"
            className="inline-flex items-center gap-2 rounded-md border-2 border-foreground/20 bg-card px-6 py-3 text-base font-bold text-foreground transition-smooth hover:border-brand-gold/60 hover:text-brand-gold-deep"
           aria-label="Call (719) 419-3935">
            <Phone className="h-4 w-4" />
            (719) 419-3935
          </a>
        </div>
        <p className="mt-6 text-sm text-muted-foreground">
          Starting at a flat fee — exact pricing quoted in writing within 48 hours of your discovery call.
        </p>
      </PageHero>

      {/* Intro */}
      <section className="bg-background py-14 lg:py-16">
        <div className="mx-auto max-w-3xl px-5 lg:px-8">
          <p className="speakable-intro text-lg leading-relaxed text-foreground/85">
            Launching a new Colorado business in 2026 means stitching together a
            website, branding, domain, email, CRM, business phone, Google Business
            Profile, AI chatbot, and content strategy. Most owners try to do it
            with five different vendors and end up with five different headaches.
            The Startup Bundle is the opposite: one vendor, one timeline, one
            bill, one person who owns the outcome.
          </p>
        </div>
      </section>

      {/* What's included */}
      <section className="border-y border-border bg-section-alt py-16 lg:py-20">
        <div className="mx-auto max-w-6xl px-5 lg:px-8">
          <h2 className="text-3xl font-extrabold text-foreground sm:text-4xl">
            What's included
          </h2>
          <p className="mt-3 max-w-2xl text-base text-muted-foreground">
            Nine pieces of digital infrastructure, built in parallel, launched
            together in 30 days.
          </p>
          <div className="mt-10 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
            {INCLUSIONS.map((item) => {
              const Icon = item.icon;
              return (
                <div
                  key={item.title}
                  className="rounded-2xl border border-border bg-card p-6 shadow-card"
                >
                  <Icon className="h-7 w-7 text-brand-gold-deep" />
                  <h3 className="mt-4 text-lg font-extrabold text-foreground">
                    {item.title}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-foreground/85">
                    {item.body}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Who this is for */}
      <section className="bg-background py-16 lg:py-20">
        <div className="mx-auto max-w-3xl px-5 lg:px-8">
          <h2 className="text-3xl font-extrabold text-foreground sm:text-4xl">
            Who this is for
          </h2>
          <div className="mt-8 space-y-6">
            {WHO_FOR.map((item) => (
              <div
                key={item.title}
                className="flex gap-4 rounded-2xl border border-border bg-card p-6 shadow-card"
              >
                <CheckCircle2 className="mt-1 h-6 w-6 shrink-0 text-brand-gold-deep" />
                <div>
                  <h3 className="text-lg font-extrabold text-foreground">{item.title}</h3>
                  <p className="mt-2 text-base leading-relaxed text-foreground/85">{item.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Versus */}
      <section className="border-y border-border bg-section-alt py-16 lg:py-20">
        <div className="mx-auto max-w-5xl px-5 lg:px-8">
          <h2 className="text-3xl font-extrabold text-foreground sm:text-4xl">
            Why one vendor beats five tools
          </h2>
          <div className="mt-8 grid gap-5 md:grid-cols-2">
            <div className="rounded-2xl border border-destructive/30 bg-destructive/5 p-6">
              <div className="text-xs font-black uppercase tracking-[0.2em] text-destructive">
                {VERSUS[0].label}
              </div>
              <p className="mt-3 text-base leading-relaxed text-foreground/85">{VERSUS[0].bad}</p>
            </div>
            <div className="rounded-2xl border border-brand-gold/40 bg-brand-gold/5 p-6">
              <div className="text-xs font-black uppercase tracking-[0.2em] text-brand-gold-deep">
                {VERSUS[1].label}
              </div>
              <p className="mt-3 text-base leading-relaxed text-foreground/85">{VERSUS[1].good}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="bg-background py-16 lg:py-20">
        <div className="mx-auto max-w-3xl px-5 lg:px-8">
          <h2 className="text-3xl font-extrabold text-foreground sm:text-4xl">
            How it works
          </h2>
          <ol className="mt-8 space-y-6">
            {TIMELINE.map((t) => (
              <li
                key={t.step}
                className="rounded-2xl border border-border bg-card p-6 shadow-card"
              >
                <div className="flex items-center gap-3">
                  <Compass className="h-5 w-5 text-brand-gold-deep" />
                  <span className="text-xs font-black uppercase tracking-[0.2em] text-brand-gold-deep">
                    {t.step}
                  </span>
                </div>
                <h3 className="mt-3 text-lg font-extrabold text-foreground">{t.title}</h3>
                <p className="mt-2 text-base leading-relaxed text-foreground/85">{t.body}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-y border-border bg-section-alt py-16 lg:py-20">
        <div className="mx-auto max-w-3xl px-5 lg:px-8">
          <h2 className="text-3xl font-extrabold text-foreground sm:text-4xl">FAQ</h2>
          <Accordion
            type="single"
            collapsible
            className="mt-8 divide-y divide-border rounded-2xl border border-border bg-card shadow-card"
          >
            {FAQS.map((f, i) => (
              <AccordionItem key={f.q} value={`faq-${i}`} className="border-0 px-5 sm:px-6">
                <AccordionTrigger className="py-5 text-left text-base font-bold text-foreground hover:no-underline">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="faq-answer pb-5 pt-0 text-base leading-relaxed text-foreground/85">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-background py-20">
        <div className="mx-auto max-w-3xl px-5 text-center lg:px-8">
          <h2 className="text-3xl font-extrabold text-foreground sm:text-4xl">
            Ready to launch?
          </h2>
          <p className="mt-4 text-lg text-muted-foreground">
            Book a 30-minute discovery call with Chris. We'll scope your bundle,
            quote it in writing, and tell you honestly if you'd be better off with
            a smaller engagement instead.
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
            <Link
              to="/contact"
              search={{ topic: "startup-bundle" } as never}
              className="inline-flex items-center gap-2 rounded-md bg-gradient-gold px-6 py-3 text-base font-bold text-brand-black shadow-gold transition-spring hover:-translate-y-0.5"
            >
              Book a Launch Call
              <ArrowRight className="h-4 w-4" />
            </Link>
            <a
              href="tel:+17194193935"
              className="inline-flex items-center gap-2 rounded-md border-2 border-foreground/20 bg-card px-6 py-3 text-base font-bold text-foreground transition-smooth hover:border-brand-gold/60 hover:text-brand-gold-deep"
             aria-label="Call (719) 419-3935">
              <Phone className="h-4 w-4" />
              (719) 419-3935
            </a>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}
